import { defineComponent, ref, Ref, ComponentInstance, useModel, nextTick } from "vue";
import { addScopedGlobalListener, VueProp } from "../core/vueHelpers";
import { convertTo12Hour, now } from "../../skyward.base.time";
import FooterToolbar from "../datePicker/components/FooterToolbar";
import TimeSelector, { TimeSelectorExposed } from "./components/TimeSelector";
import { Time } from "./TimePicker";

const TimePickerModalClasses =
{
    modal: "dateTimePickerModal",
    modalSecondary: "dateTimePickerModal--time"
};

export default /*#__PURE__*/ defineComponent(
{
    name: "timePickerModal",
    emits: 
    {
        "update:time": (_value: Time) => true,
    },
    props: 
    {
        close: VueProp<() => void>(Function),
        inputRef: VueProp<Ref<HTMLInputElement | undefined, HTMLInputElement | undefined>>(Object),
        time: VueProp<Time>(Object),
    },
    setup(props, context)
    {
        const time = useModel(props, "time");

        const hour = ref<number | undefined>(time.value.hour);
        const minute = ref<number | undefined>(time.value.minute);
        const amPm = ref<"AM" | "PM">(time.value.amPm ?? "AM");

        const modal = ref<HTMLDivElement>();
        const timeSelectorRef = ref<ComponentInstance<typeof TimeSelector> & TimeSelectorExposed>();

        context.expose(
        {
            focusHourInput() { timeSelectorRef.value?.focusHourInput(); },
            focusMinuteInput() { timeSelectorRef.value?.focusMinuteInput(); },
            focusAmPmToggle() { timeSelectorRef.value?.focusAmPmToggle(); }
        });

        addScopedGlobalListener(document.body, "keydown", handleGlobalKeydown);
        addScopedGlobalListener(document.body, "keypress", handleGlobalKeypress);

        return () => (
            <div class={[TimePickerModalClasses.modal, TimePickerModalClasses.modalSecondary]} ref={modal}>
                <TimeSelector
                    ref={timeSelectorRef}
                    hour={hour.value}
                    minute={minute.value}
                    amPm={amPm.value}
                    onUpdate:hour={value => (hour.value = value)}
                    onUpdate:minute={value => (minute.value = value)}
                    onUpdate:amPm={value => (amPm.value = value)}
                />
                <FooterToolbar buttonText="Now" onButtonClick={onNowClick} onDoneClick={saveAndClose} />
            </div>
        );

        function saveAndClose()
        {
            updateValue();
            props.close();
        }

        function handleGlobalKeypress(event: JQuery.KeyPressEvent)
        {
            const key = event.key.toUpperCase();

            if (key === "ENTER")
            {
                saveAndClose();
            }
        }

        async function handleGlobalKeydown(event: JQuery.KeyDownEvent)
        {
            const key = event.key.toUpperCase();

            switch (key)
            {
                case "ARROWUP":
                case "ARROWDOWN":
                case "PAGEUP":
                case "PAGEDOWN":
                case "HOME":
                case "END":
                    event.preventDefault();
                    break;
                case "C":
                case "T":
                case "N":
                    await setToNow()
                    return;
            }

            if (event.altKey && (key === "ARROWUP" || key === "ARROWDOWN"))
            {
                props.inputRef.value?.focus();
                return;
            }
        }

        function updateValue()
        {
            if ((hour.value === undefined || isNaN(hour.value)) && (minute.value === undefined || isNaN(minute.value)))
            {
                const newTime: Time =
                {
                    hour: undefined,
                    minute: undefined,
                    amPm: undefined,
                };
                time.value = newTime;
                return;
            }

            const newTime: Time =
            {
                hour: hour.value !== undefined && !isNaN(hour.value) ? hour.value : 12,
                minute: minute.value !== undefined && !isNaN(minute.value) ? minute.value : 0,
                amPm: amPm.value,
            };
            time.value = newTime;
        }

        async function onNowClick()
        {
            await setToNow();
            saveAndClose();
        }

        async function setToNow()
        {
            const today = now();

            const currentHour = today.getHours();
            hour.value = convertTo12Hour(currentHour);
            if (currentHour >= 12)
            {
                amPm.value = "PM";
            }
            else
            {
                amPm.value = "AM";
            }

            minute.value = today.getMinutes();

            await nextTick();
        }
    }
});
